import type { AccountNo } from '@shared/products-types';
import type { Amount, Fee, SignInfo } from '@shared/core-types';

/** Double checked, matched with apiary on 19-04-2021 */
export interface BuildingSociety {
    accountno?: AccountNo;
    agreementNumber?: string;
    alias?: string;
    balance: Amount;
    beneficiaries?: string[];
    contractHolders?: string[];
    creditInterestRate?: number;
    debitInterestRate?: number;
    debitInterestRateType?: BuildingDebitInterestRateType;
    description?: string;
    flags?: Flag[];
    features?: Feature[];
    id: string;
    loan?: BuildSocietyLoanData;
    product: string;
    productI18N: string;
    productSubType?: string;
    saving?: BuildSocietySavingData;
    signInfo?: SignInfo;
    status?: 'ACTIVE' | 'CLOSED' | 'PAUSED' | 'PROCESSING';
    statusInfoI18N?: string;
    type: 'BUILD_LOAN' | 'BUILD_SAVING';
    signingDate?: string;
    statementDistribution?: 'GEORGE' | 'NONE' | 'EMAIL' | 'POST';
}

type Feature = 'selfService';

export type BuildingSocietyLoan = BuildingSociety & {
    type: 'BUILD_LOAN';
    loan: BuildSocietyLoanData;
};

export type BuildingSocietySaving = BuildingSociety & {
    type: 'BUILD_SAVING';
    saving: BuildSocietySavingData;
};

export interface BuildSocietySavingData {
    agreedMonthlySavings?: Amount;
    bonusBearingDepositDone?: Amount;
    bonusBearingDepositToPay?: Amount;
    bonusBearingMonthlySavings?: Amount;
    bonusBearingYearlySavings?: Amount;
    currentSavings?: Amount;
    feeInfo?: Fee[];
    initialDepositDate?: string;
    minimumSavings?: Amount;
    minimumSavingsPeriod?: IntervalWithValue;
    maximumSavings?: Amount;
    nextSavingsDate?: string;
    nextPauseDate?: string;
    nextChangeRateDate?: string;
    nextChangeSavingsDate?: string;
    savingsFrequency?: SavingFrequency;
    savingsInPeriod?: Amount;
    subProductI18N?: string;
    expiryDate?: string;
    targetAmount?: Amount;
    terminationPayout?: IntervalWithValue;
    depositWithFees?: Amount;
    interestAmountAfterTax?: Amount;
    totalAmountStateSupport?: Amount;
    loanEntitlementDate?: string;
    startDate?: string;
}

export interface BuildSocietyLoanData {
    additionalSavings?: Amount;
    interestRateFromDate?: string;
    interestRateToDate?: string;
    loanAmount: Amount;
    loanInstallment: Amount;
    maturityDate?: string;
    nextInstallmentDate?: string;
    paymentInsurance?: Amount;
    savingsInPeriod: Amount;
    periodicity: 'ONCE' | 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'HALFYEARLY' | 'YEARLY';
    totalInstallment: Amount;
    startDate?: string;
}

export enum BuildingSavingType {
    BUILD_SAVING = 'BUILD_SAVING',
    BUILD_LOAN = 'BUILD_LOAN',
}

export type BuildingDebitInterestRateType = 'FIXED' | 'VARIABLE';

type Flag =
    | 'notRequestedBonus'
    | 'changeSavingsAllowed'
    | 'changeSavingsAlreadyUsed'
    | 'changeSavingsWithinDrawDownCycle'
    | 'changeSavingsTemporaryNotPossible'
    | 'pauseSavingsAllowed'
    | 'pauseSavingAlreadyUsed'
    | 'pauseSavingsAlreadyUsed'
    | 'pauseSavingsAlreadyWithinDrawDownCycle'
    | 'pauseSavingsTemporaryNotPossible'
    | 'stateSupportBonusAgreed'
    | 'contractUnderExamination'
    | 'selfServiceAllowed'
    | 'junior';

type SavingFrequency =
    | 'DAILY'
    | 'WEEKLY'
    | 'MONTHLY'
    | 'QUARTERLY'
    | 'HALFYEARLY'
    | 'YEARLY'
    | '3_YEARLY'
    | '5_YEARLY';

export type BuildingSavingInterval =
    | 'ONCE'
    | 'DAILY'
    | 'WEEKLY'
    | 'MONTHLY'
    | 'QUARTERLY'
    | 'HALFYEARLY'
    | 'YEARLY';

interface IntervalWithValue {
    interval: BuildingSavingInterval;
    value: number;
}
